import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useOrganization } from "./useOrganization";

export interface SavedCard {
  id: string;
  stripe_payment_method_id: string;
  card_brand: string;
  card_last4: string;
  card_exp_month: number;
  card_exp_year: number;
  is_default: boolean;
  is_mit_enabled: boolean;
  created_at: string;
}

export function useSavedCards() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ["saved-cards", user?.id, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("player_payment_methods" as any)
        .select("*")
        .eq("user_id", user!.id)
        .eq("organization_id", organization!.id)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as SavedCard[];
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["saved-cards"] });

  const addCard = useMutation({
    mutationFn: async () => {
      // 1. Create SetupIntent
      const { data, error } = await supabase.functions.invoke("create-setup-intent", {
        body: { organization_id: organization!.id },
      });
      if (error) throw new Error(error.message);
      return data as { client_secret: string; customer_id: string };
    },
  });

  const saveCard = useMutation({
    mutationFn: async (setupIntentId: string) => {
      const { data, error } = await supabase.functions.invoke("save-payment-method", {
        body: { setup_intent_id: setupIntentId, organization_id: organization!.id },
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: invalidate,
  });

  const queryKey = ["saved-cards", user?.id, organization?.id];

  const deleteCard = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { error } = await supabase.functions.invoke("delete-payment-method", {
        body: { payment_method_id: paymentMethodId },
      });
      if (error) throw new Error(error.message);
    },
    onMutate: async (paymentMethodId) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<SavedCard[]>(queryKey);
      qc.setQueryData<SavedCard[]>(queryKey, (old) => (old ?? []).filter((c) => c.id !== paymentMethodId));
      return { prev };
    },
    onError: (_err, _id, context) => {
      if (context?.prev) qc.setQueryData(queryKey, context.prev);
    },
    onSettled: invalidate,
  });

  const setDefault = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { error } = await supabase.functions.invoke("set-default-payment-method", {
        body: { payment_method_id: paymentMethodId },
      });
      if (error) throw new Error(error.message);
    },
    onMutate: async (paymentMethodId) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<SavedCard[]>(queryKey);
      qc.setQueryData<SavedCard[]>(queryKey, (old) =>
        (old ?? []).map((c) => ({ ...c, is_default: c.id === paymentMethodId }))
      );
      return { prev };
    },
    onError: (_err, _id, context) => {
      if (context?.prev) qc.setQueryData(queryKey, context.prev);
    },
    onSettled: invalidate,
  });

  return { cards, isLoading, addCard, saveCard, deleteCard, setDefault };
}
